<script setup lang="ts">
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import ToggleSwitch from "@/program/components/support-program/SupportProgramNotificationToggle/ToggleSwitch.vue";
import {computed, onMounted, reactive, watch} from "vue";
import {pollApi} from "@/container";
import {Poll} from "@/program/interfaces/Program";

const props = defineProps<{
    polls: Poll[]
}>();

/** Polls displayed inside the program card have no toggle: they are always enabled. */
const togglablePolls = computed(() => props.polls?.filter(poll => !poll.display_in_program) ?? []);

const pollStates = reactive<Record<number, { enabled: boolean; loading: boolean }>>({});

onMounted(() => {
    togglablePolls.value.forEach(poll => {
        if (!pollStates[poll.id]) {
            pollStates[poll.id] = {
                enabled: poll.enabled,
                loading: false
            };
        }
    });
});

togglablePolls.value.forEach(poll => {
    watch(
        () => pollStates[poll.id]?.enabled,
        (newValue, oldValue) => {
            if (oldValue !== undefined && newValue !== oldValue) {
                togglePoll(poll.id);
            }
        }
    );
});

/**
 * Toggle the enabled state of a poll
 *
 * @param pollId - The poll id
 */
const togglePoll = async (pollId: number) => {
    const state = pollStates[pollId];
    if (!state) return;

    state.loading = true;
    const previousEnabled = state.enabled;

    try {
        const response = await pollApi().toggle(pollId);
        state.enabled = response.enabled;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
        state.enabled = previousEnabled;
    } finally {
        state.loading = false;
    }
};


</script>

<template>
  <div v-if="togglablePolls.length" class="flex flex-col gap-5">
    <BaseCard v-for="poll in togglablePolls" :key="poll.id">
      <BaseCardBody class="flex gap-5">
        <div>
          <h3>
            Sondage {{ poll.name }}
          </h3>
          <p class="text-sm">
            {{ poll.description }}
          </p>
        </div>
        <div class="px-5">
          <ToggleSwitch
            v-if="pollStates[poll.id]"
            v-model="pollStates[poll.id].enabled"
            :is-loading="pollStates[poll.id].loading"
          />
        </div>
      </BaseCardBody>
    </BaseCard>
  </div>
</template>

<style scoped>

</style>
